import React, { useRef, useState } from "react"
import classNames from "classnames"
import { useEditor, useValue } from "../../editor-context"
import useDebounce from "../../hooks/use-debounce"
import useEventListener from "../../hooks/use-event-listener"
import useTransitionElement from "../../hooks/use-transition-element"
import { useSelectionPosition } from "../../hooks/use-selection"
import { isTextSelected } from "../../utils/slate"

import Toolbar from "./toolbar-content"

import "./toolbar.css"
import isHotkey from "is-hotkey"

function SelectionToolbar({ buttons, exclude = [] }) {
  const editor = useEditor()
  const value = useValue()
  const toolbar = useRef()

  const [transitioning, startTransition] = useTransitionElement(toolbar.current)

  const [visible, setVisible] = useState(false)
  const [menu, setMenu] = useState(null)

  const openMenu = menu => {
    setMenu(menu)
  }

  const closeMenu = () => {
    setMenu(null)
  }

  const { styles, arrowStyles } = useSelectionPosition(
    toolbar.current,
    {
      placement: "top",
      debounce: menu ? -1 : visible ? 300 : 100
    },
    [menu]
  )

  const show = () => {
    startTransition()
    setVisible(true)
  }

  const hide = () => {
    startTransition()
    setVisible(false)

    // Wait until the toolbar is hidden before closing the menu.
    setTimeout(() => {
      closeMenu()
    }, 300)
  }

  const textSelected = isTextSelected(value)

  const [handleSelectionChange] = useDebounce(() => {
    if (!editor) return hide()
    const isLink = editor.isLink()
    const shouldShow = (textSelected || isLink) && !visible
    const shouldHide = !textSelected && !menu && visible

    if (shouldShow) return show()
    if (shouldHide) return hide()
  }, 200)

  const handleKeyDown = event => {
    event.stopPropagation()

    if (!visible) return

    buttons.find((button, index) => {
      if (!button.shortcut) return false

      if (isHotkey(button.shortcut, event)) {
        openMenu(index)
        return true
      }
      return false
    })
  }

  useEventListener(document, "selectionchange", handleSelectionChange)
  useEventListener(window, "resize", handleSelectionChange)
  useEventListener(document, "keydown", handleKeyDown)

  const type = value.focusBlock && value.focusBlock.type

  const className = classNames("convertkit-editor-selection-toolbar", {
    visible: visible && !exclude.includes(type),
    active: isTextSelected(value) && !exclude.includes(type),
    transitioning
  })

  return (
    <Toolbar
      ref={toolbar}
      className={className}
      editor={editor}
      buttons={buttons}
      menu={{
        active: menu != null,
        index: menu,
        close: closeMenu,
        open: openMenu
      }}
      style={styles}
      arrowStyle={arrowStyles}
    />
  )
}

export default SelectionToolbar
